import { useEffect, useState } from 'react';

const catchcopies = [
  { num: '01', text: '会議が多いのに、何も決まらない。' },
  { num: '02', text: 'その会議、設計されていますか？' },
  { num: '03', text: '話すだけの会議から、決まって動く会議へ。' },
];

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0e0e0e 0%, #1a0e0a 55%, #111111 100%)' }}
    >
      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* Red glow */}
      <div
        className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(192,57,43,0.18) 0%, transparent 70%)' }}
      />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-10 py-28">
        {/* Badge */}
        <div
          className="mb-10 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-bold tracking-widest uppercase border border-[#C0392B]/40 text-[#C0392B] rounded-full"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#C0392B] inline-block animate-pulse" />
            会議設計
          </span>
        </div>

        {/* Catchcopies */}
        <div className="space-y-4 mb-12">
          {catchcopies.map((c, i) => (
            <div
              key={i}
              className="flex items-baseline gap-5 transition-all duration-700"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateX(0)' : 'translateX(-30px)',
                transitionDelay: `${0.1 + i * 0.12}s`,
              }}
            >
              <span
                className="text-xs font-bold text-[#C0392B] flex-shrink-0 tabular-nums"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {c.num}
              </span>
              <span
                className="border-l border-white/10 pl-5"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                <span
                  className={`font-bold leading-tight transition-all duration-300 ${
                    i === 2
                      ? 'text-3xl md:text-5xl text-white'
                      : 'text-xl md:text-2xl text-white/40'
                  }`}
                >
                  {c.text}
                </span>
              </span>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div
          className="w-12 h-0.5 bg-[#C0392B] mb-8 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transitionDelay: '0.5s' }}
        />

        {/* Sub text */}
        <div
          className="mb-10 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '0.55s' }}
        >
          <p
            className="text-base md:text-lg text-white/65 leading-loose max-w-xl"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            意思決定が遅い組織の問題は、<strong className="text-white font-bold">「人」ではなく「会議の設計」</strong>にある。<br />
            中小企業の会議を根本から再設計し、組織を動かします。
          </p>
        </div>

        {/* CTAs */}
        <div
          className="flex flex-col sm:flex-row gap-4 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '0.65s' }}
        >
          <button
            onClick={() => scrollTo('#cta')}
            className="px-8 py-4 bg-[#C0392B] text-white font-bold text-base rounded-md hover:bg-[#a93226] transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            無料診断（5分）→ 今すぐ試す
          </button>
          <button
            onClick={() => scrollTo('#problem')}
            className="px-8 py-4 bg-white/8 border border-white/20 text-white font-medium text-base rounded-md hover:bg-white/15 transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            会議の問題を診てみる
          </button>
        </div>

        {/* Stats */}
        <div
          className="mt-16 grid grid-cols-3 gap-4 max-w-lg transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transitionDelay: '0.8s' }}
        >
          {[
            { val: '80社+', label: '支援実績' },
            { val: '42%', label: '会議時間削減' },
            { val: '3倍', label: '意思決定スピード' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div
                className="text-2xl md:text-3xl font-bold text-[#C0392B]"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {s.val}
              </div>
              <div className="text-xs text-white/40 mt-1" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce opacity-40">
        <span className="text-white text-xs" style={{ fontFamily: 'Noto Sans JP' }}>scroll</span>
        <i className="ri-arrow-down-line text-white" />
      </div>
    </section>
  );
}
